import { useEffect, useRef, useState } from 'react';

import { TabPage, TabView, TextArea } from 'fluxo-ui';
import { EditIcon, EyeIcon, HelpIcon } from 'fluxo-ui/icons';

import type { ComponentEvent } from 'fluxo-ui';

interface MarkdownEditorProps {
    value?: string;
    onChange?: (value: string) => void;
    placeholder?: string;
    rows?: number;
    disabled?: boolean;
    showHelp?: boolean;
    label?: string;
}

const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
    value = '',
    onChange,
    placeholder = 'Enter your text here... (Markdown supported)',
    rows = 10,
    disabled = false,
    showHelp = true,
    label,
}) => {
    const [activeTab, setActiveTab] = useState(0);
    const [text, setText] = useState(value);
    const containerRef = useRef<HTMLDivElement>(null);

    // Keep internal state in sync when the parent updates the value
    // (e.g. question loaded asynchronously in the edit page)
    useEffect(() => {
        setText(value);
    }, [value]);

    const handleChange = (e: ComponentEvent<string>) => {
        setText(e.value);
        onChange?.(e.value);
    };

    const insertMarkdown = (before: string, after: string = '') => {
        // Scope to THIS editor — multiple MarkdownEditors can be mounted at once
        const textarea = containerRef.current?.querySelector('textarea');
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = text.substring(start, end);
        const newText = text.substring(0, start) + before + selectedText + after + text.substring(end);

        setText(newText);
        onChange?.(newText);

        // Restore focus and selection
        setTimeout(() => {
            textarea.focus();
            const newCursorPos = start + before.length + selectedText.length;
            textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
    };

    const markdownButtons = [
        { label: 'Bold', action: () => insertMarkdown('**', '**'), icon: 'B', className: 'font-bold' },
        { label: 'Italic', action: () => insertMarkdown('*', '*'), icon: 'I', className: 'italic' },
        { label: 'Code', action: () => insertMarkdown('`', '`'), icon: '</>', className: 'font-mono text-xs' },
        { label: 'Link', action: () => insertMarkdown('[', '](url)'), icon: '🔗', className: '' },
        {
            label: 'Heading',
            action: () => insertMarkdown('\n## ', ''),
            icon: 'H',
            className: 'font-bold text-lg',
        },
        { label: 'List', action: () => insertMarkdown('\n- ', ''), icon: '•', className: '' },
        {
            label: 'Code Block',
            action: () => insertMarkdown('\n```\n', '\n```\n'),
            icon: '{}',
            className: 'font-mono text-xs',
        },
    ];

    // Simple markdown preview (basic implementation)
    const renderMarkdown = (markdown: string) => {
        const html = markdown
            // Headers
            .replace(/^### (.*$)/gim, '<h3 class="text-lg font-bold mt-4 mb-2">$1</h3>')
            .replace(/^## (.*$)/gim, '<h2 class="text-xl font-bold mt-4 mb-2">$1</h2>')
            .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-4 mb-2">$1</h1>')
            // Bold
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            // Italic
            .replace(/\*(.*?)\*/g, '<em>$1</em>')
            // Code inline
            .replace(/`(.*?)`/g, '<code class="bg-slate-200 dark:bg-slate-700 px-1 py-0.5 rounded text-sm">$1</code>')
            // Links
            .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-teal-600 hover:underline">$1</a>')
            // Lists
            .replace(/^- (.*$)/gim, '<li class="ml-4">$1</li>')
            // Line breaks
            .replace(/\n/g, '<br />');

        return html;
    };

    return (
        <div ref={containerRef} className="space-y-3">
            {label && <label className="block text-sm font-medium text-slate-900 dark:text-white">{label}</label>}

            {/* Markdown Toolbar */}
            <div className="flex items-center gap-1 p-2 bg-slate-50 dark:bg-slate-800 rounded-t-lg border border-b-0 border-slate-200 dark:border-slate-700">
                {markdownButtons.map((btn, index) => (
                    <button
                        key={index}
                        onClick={btn.action}
                        disabled={disabled}
                        title={btn.label}
                        className={`px-3 py-1.5 text-sm rounded text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer ${btn.className}`}
                    >
                        {btn.icon}
                    </button>
                ))}
            </div>

            {/* Editor/Preview Tabs */}
            <TabView
                activeIndex={activeTab}
                onTabChange={(e) => setActiveTab(e.index)}
                className="border border-slate-200 dark:border-slate-700 rounded-b-lg"
            >
                <TabPage header="Edit" leftIcon={EditIcon}>
                    <div className="p-0">
                        <TextArea
                            value={text}
                            onChange={handleChange}
                            placeholder={placeholder}
                            rows={rows}
                            disabled={disabled}
                            autoResize={false}
                            className="border-0 rounded-none"
                        />
                    </div>
                </TabPage>
                <TabPage header="Preview" leftIcon={EyeIcon}>
                    <div className="p-4 min-h-[200px] prose dark:prose-invert max-w-none">
                        {text ? (
                            <div className="text-slate-900 dark:text-white" dangerouslySetInnerHTML={{ __html: renderMarkdown(text) }} />
                        ) : (
                            <div className="text-slate-400 dark:text-slate-500 text-center py-12">Preview will appear here...</div>
                        )}
                    </div>
                </TabPage>
            </TabView>

            {/* Help Section */}
            {showHelp && (
                <details className="text-sm">
                    <summary className="cursor-pointer text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 flex items-center gap-2">
                        <HelpIcon className="w-4 h-4" />
                        <span>Markdown & Media Placeholders Help</span>
                    </summary>
                    <div className="mt-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 space-y-3">
                        <div>
                            <h4 className="font-semibold text-slate-900 dark:text-white mb-2">Markdown Syntax</h4>
                            <ul className="space-y-1 text-slate-600 dark:text-slate-400">
                                <li>
                                    <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">**bold**</code> for{' '}
                                    <strong>bold text</strong>
                                </li>
                                <li>
                                    <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">*italic*</code> for <em>italic text</em>
                                </li>
                                <li>
                                    <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">`code`</code> for inline code
                                </li>
                                <li>
                                    <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">[link text](url)</code> for links
                                </li>
                                <li>
                                    <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">## Heading</code> for headings
                                </li>
                                <li>
                                    <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">- item</code> for lists
                                </li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold text-slate-900 dark:text-white mb-2">Media Placeholders</h4>
                            <ul className="space-y-1 text-slate-600 dark:text-slate-400">
                                <li>
                                    <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">{'{image:id}'}</code> - Insert an image
                                </li>
                                <li>
                                    <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">{'{video:id}'}</code> - Insert a video
                                </li>
                                <li>
                                    <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">{'{doc:id}'}</code> - Insert a document
                                </li>
                                <li>
                                    <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">{'{audio:id}'}</code> - Insert an audio file
                                </li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold text-slate-900 dark:text-white mb-2">
                                Fill in the Blanks (for Fill Blanks question type)
                            </h4>
                            <p className="text-slate-600 dark:text-slate-400">
                                Use <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">$answer$</code> to mark blank spaces in your
                                text
                            </p>
                        </div>
                    </div>
                </details>
            )}
        </div>
    );
};

export default MarkdownEditor;
